<script setup>
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue';
import { createPoolSurgeMomentum, detectPoolSurge, normalizePoolValues, POOL_ITEMS, poolBarRatios, poolTweenDuration } from '../domain/pool.js';

const props = defineProps({ pool: { type: Object, default: () => ({}) }, t: { type: Function, required: true } });
const initial = normalizePoolValues(props.pool);
const displayValues = reactive([...initial]);
const increasing = reactive([false, false, false, false, false]);
const frames = [0, 0, 0, 0, 0];
const lastPaintAt = [0, 0, 0, 0, 0];
const colorTimers = [0, 0, 0, 0, 0];
const ratios = computed(() => poolBarRatios(displayValues));
const surgingIndex = ref(-1);
const surgeIntensity = ref(0);
const burstSerial = ref(0);
let previousTargets = [...initial];
let surgeMomentum = createPoolSurgeMomentum();
let surgeTimer = 0;
const renderIntervalMs = 50;

function formatValue(value) {
  return Math.max(0, Math.floor(Number(value) || 0)).toLocaleString('en-US');
}

function valueFontSize(value) {
  const length = formatValue(value).length;
  if (length >= 13) return 'var(--pool-value-font-size-long, 10px)';
  if (length >= 10) return 'var(--pool-value-font-size-medium, 12px)';
  return 'var(--pool-value-font-size-short, 16px)';
}

function animateValue(index, target) {
  window.cancelAnimationFrame(frames[index]);
  window.clearTimeout(colorTimers[index]);
  const start = Math.max(0, Number(displayValues[index]) || 0);
  const safeTarget = Math.max(0, Math.floor(Number(target) || 0));
  if (Math.floor(start) === safeTarget) return;
  const startedAt = performance.now();
  const duration = poolTweenDuration(start, safeTarget);
  increasing[index] = safeTarget > start;
  const tick = (now) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    if (progress >= 1 || now - lastPaintAt[index] >= renderIntervalMs) {
      displayValues[index] = Math.floor(start + ((safeTarget - start) * eased));
      lastPaintAt[index] = now;
    }
    if (progress < 1) {
      frames[index] = window.requestAnimationFrame(tick);
      return;
    }
    displayValues[index] = safeTarget;
    colorTimers[index] = window.setTimeout(() => { increasing[index] = false; }, 120);
  };
  frames[index] = window.requestAnimationFrame(tick);
}

watch(() => normalizePoolValues(props.pool), (targets) => {
  const surge = detectPoolSurge(surgeMomentum, previousTargets, targets, performance.now());
  surgeMomentum = surge.momentum;
  previousTargets = [...targets];
  if (surge.surgeIndex >= 0) {
    surgingIndex.value = surge.surgeIndex;
    surgeIntensity.value = surge.intensity;
    burstSerial.value += 1;
    window.clearTimeout(surgeTimer);
    surgeTimer = window.setTimeout(() => {
      surgingIndex.value = -1;
      surgeIntensity.value = 0;
      surgeTimer = 0;
    }, 1500);
  }
  targets.forEach((target, index) => animateValue(index, target));
});

onBeforeUnmount(() => {
  frames.forEach((frame) => window.cancelAnimationFrame(frame));
  colorTimers.forEach((timer) => window.clearTimeout(timer));
  window.clearTimeout(surgeTimer);
});
</script>

<template>
  <div class="game-video-pool">
    <div class="game-video-pool-title">{{ t('vueDesktop.label.pool', 'Pool') }}</div>
    <div v-for="(item,index) in POOL_ITEMS" :key="item.key" class="game-pool-row" :class="[item.className,{ surging:surgingIndex===index }]" :style="{ '--surge-ratio':ratios[index], '--surge-intensity':surgeIntensity }">
      <i class="game-pool-accent" />
      <span>{{ t(`labelCompact.${item.key}`, item.label) }}</span>
      <span class="game-pool-bar-track">
        <i aria-hidden="true" :style="{ width: displayValues[index] > 0 ? `max(10px, ${ratios[index] * 100}%)` : '0' }" />
        <b :class="{ increasing: increasing[index] }" :style="{ fontSize: valueFontSize(displayValues[index]) }">{{ formatValue(displayValues[index]) }}</b>
      </span>
      <span v-if="surgingIndex===index" :key="burstSerial" class="game-pool-particle-burst" aria-hidden="true"><i v-for="spark in 24" :key="spark" /></span>
    </div>
  </div>
</template>
